"use client";

import { useState } from "react";
import { useToolUi } from "@/components/tools/tool-ui";
import { writeToClipboard } from "@/lib/clipboard";
import { pick, randomBelow } from "@/lib/tool-kit/random-fun/rng";
import { cleanKeyword } from "@/lib/tool-kit/random-tools/username";

const ADJECTIVES = [
  "amber",
  "ancient",
  "arctic",
  "atomic",
  "azure",
  "bold",
  "brave",
  "breezy",
  "bright",
  "calm",
  "cheerful",
  "clever",
  "cosmic",
  "cozy",
  "crimson",
  "curious",
  "daring",
  "dashing",
  "eager",
  "electric",
  "emerald",
  "epic",
  "fearless",
  "fluffy",
  "frosty",
  "gentle",
  "gifted",
  "golden",
  "graceful",
  "happy",
  "hidden",
  "humble",
  "jolly",
  "keen",
  "kind",
  "legendary",
  "lively",
  "loyal",
  "lucky",
  "lunar",
  "magic",
  "mellow",
  "mighty",
  "misty",
  "noble",
  "peppy",
  "playful",
  "proud",
  "quick",
  "quiet",
  "radiant",
  "rapid",
  "royal",
  "silent",
  "silver",
  "solar",
  "speedy",
  "stellar",
  "sunny",
  "swift",
  "turbo",
  "velvet",
  "vivid",
  "wandering",
  "wild",
  "witty",
];

const NOUNS = [
  "acorn",
  "anchor",
  "arrow",
  "badger",
  "beacon",
  "bear",
  "blossom",
  "breeze",
  "brook",
  "canyon",
  "cascade",
  "cedar",
  "comet",
  "compass",
  "coral",
  "crane",
  "dolphin",
  "dragon",
  "eagle",
  "ember",
  "falcon",
  "fern",
  "firefly",
  "fox",
  "galaxy",
  "glacier",
  "harbor",
  "hawk",
  "horizon",
  "koala",
  "lantern",
  "lion",
  "lotus",
  "maple",
  "meadow",
  "meteor",
  "moose",
  "mountain",
  "nebula",
  "ocean",
  "orbit",
  "otter",
  "owl",
  "panda",
  "pebble",
  "penguin",
  "phoenix",
  "pine",
  "pixel",
  "prairie",
  "raven",
  "river",
  "robin",
  "rocket",
  "sparrow",
  "summit",
  "thunder",
  "tiger",
  "voyager",
  "willow",
  "wolf",
  "zephyr",
];

type Separator = "none" | "underscore" | "dot";

const SEPARATORS: { value: Separator; label: string; char: string }[] = [
  { value: "none", label: "None", char: "" },
  { value: "underscore", label: "Underscore (_)", char: "_" },
  { value: "dot", label: "Dot (.)", char: "." },
];

function pickWordPair(keyword: string): [string, string] {
  if (keyword.length === 0) {
    return [pick(ADJECTIVES), pick(NOUNS)];
  }
  const pattern = randomBelow(3);
  if (pattern === 0) {
    return [keyword, pick(NOUNS)];
  }
  if (pattern === 1) {
    return [pick(ADJECTIVES), keyword];
  }
  return [pick(ADJECTIVES), pick(NOUNS)];
}

function capitalizeWord(word: string): string {
  if (word.length === 0) {
    return word;
  }
  return word[0].toUpperCase() + word.slice(1);
}

function buildUsername(options: {
  keyword: string;
  addNumbers: boolean;
  capitalize: boolean;
  separatorChar: string;
}): string {
  const pair = pickWordPair(options.keyword);
  const words = options.capitalize
    ? pair.map(capitalizeWord)
    : pair.map((w) => w.toLowerCase());
  let name = words.join(options.separatorChar);
  if (options.addNumbers) {
    const digits = String(randomBelow(90) + 10);
    name = `${name}${digits}`;
  }
  return name;
}

/** English strings. A translated content file overrides these through `ui`. */
export const USERNAME_GENERATOR_STRINGS = {
  keywordLabel: "Keyword (optional) — a hobby, nickname, or interest",
  keywordPlaceholder: "e.g. chess, astro, birdie",
  addNumbers: "Add numbers",
  capitalizeWords: "Capitalize words",
  separatorLabels: {
    none: "None",
    underscore: "Underscore (_)",
    dot: "Dot (.)",
  },
  generate10More: "Generate 10 More",
  generate10Usernames: "Generate 10 Usernames",
  yourUsernameIdeas: "Your Username Ideas",
  clickToCopy: "Click a username to copy it.",
  copied: "Copied!",
  ideasNote:
    "These are ideas, not reservations — whether a name is actually available depends on each platform.",
} as const;

export default function UsernameGeneratorWidget() {
  const strings = useToolUi(USERNAME_GENERATOR_STRINGS);
  const [keyword, setKeyword] = useState("");
  const [addNumbers, setAddNumbers] = useState(true);
  const [capitalize, setCapitalize] = useState(true);
  const [separator, setSeparator] = useState<Separator>("none");
  const [usernames, setUsernames] = useState<string[]>([]);
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);

  function handleGenerate() {
    const separatorChar =
      SEPARATORS.find((s) => s.value === separator)?.char ?? "";
    const cleanedKeyword = cleanKeyword(keyword);
    const results: string[] = [];
    for (let i = 0; i < 10; i += 1) {
      results.push(
        buildUsername({
          keyword: cleanedKeyword,
          addNumbers,
          capitalize,
          separatorChar,
        })
      );
    }
    setUsernames(results);
    setCopiedIndex(null);
  }

  function handleCopy(name: string, index: number) {
    writeToClipboard(name).then((copied) => {
      if (!copied) {
        return;
      }

      setCopiedIndex(index);
      setTimeout(() => setCopiedIndex(null), 2000);
    });
  }

  return (
    <>
      {/* Options */}
      <section className="space-y-3">
        <div className="space-y-2">
          <label
            className="font-medium text-foreground text-sm"
            htmlFor="username-keyword"
          >
            {strings.keywordLabel}
          </label>
          <input
            className="w-full rounded-lg border border-border bg-card px-4 py-2 text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            id="username-keyword"
            maxLength={20}
            onChange={(e) => setKeyword(e.target.value)}
            placeholder={strings.keywordPlaceholder}
            type="text"
            value={keyword}
          />
        </div>
        <div className="flex flex-wrap gap-3">
          <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm transition-colors has-[:checked]:border-primary has-[:checked]:bg-primary/5">
            <input
              checked={addNumbers}
              className="accent-primary"
              onChange={() => setAddNumbers(!addNumbers)}
              type="checkbox"
            />
            {strings.addNumbers}
          </label>
          <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm transition-colors has-[:checked]:border-primary has-[:checked]:bg-primary/5">
            <input
              checked={capitalize}
              className="accent-primary"
              onChange={() => setCapitalize(!capitalize)}
              type="checkbox"
            />
            {strings.capitalizeWords}
          </label>
        </div>
        <div className="flex flex-wrap gap-3">
          {SEPARATORS.map((s) => (
            <label
              className="flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm transition-colors has-[:checked]:border-primary has-[:checked]:bg-primary/5"
              key={s.value}
            >
              <input
                checked={separator === s.value}
                className="accent-primary"
                name="username-separator"
                onChange={() => setSeparator(s.value)}
                type="radio"
                value={s.value}
              />
              {strings.separatorLabels[s.value]}
            </label>
          ))}
        </div>
        <button
          className="rounded-lg bg-primary px-5 py-2 font-medium text-primary-foreground text-sm transition-opacity hover:opacity-90"
          onClick={handleGenerate}
          type="button"
        >
          {usernames.length > 0
            ? strings.generate10More
            : strings.generate10Usernames}
        </button>
      </section>

      {/* Results */}
      {usernames.length > 0 && (
        <section className="space-y-3">
          <h2 className="font-semibold text-foreground text-xl">
            {strings.yourUsernameIdeas}
          </h2>
          <p className="text-muted-foreground text-sm">{strings.clickToCopy}</p>
          <div className="flex flex-wrap gap-2">
            {usernames.map((name, index) => (
              <button
                className="rounded-full border border-border bg-card px-4 py-1.5 font-mono text-foreground text-sm transition-colors hover:border-primary hover:bg-primary/5"
                key={`${name}-${String(index)}`}
                onClick={() => handleCopy(name, index)}
                type="button"
              >
                {copiedIndex === index ? strings.copied : name}
              </button>
            ))}
          </div>
          <p className="text-muted-foreground text-xs">{strings.ideasNote}</p>
        </section>
      )}
    </>
  );
}
